import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  Globe,
  TrendingUp,
  FileText,
  Scale,
  BarChart3,
  Activity,
  Sun,
  Moon,
  BookOpen,
  Menu,
  X,
  ChevronDown,
  MapPin,
  Mail,
  Search,
} from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import { emitInsightsSearchQuery } from '../utils/insightsSearchBus';

const NAV_GROUPS = [
  {
    title: 'Global risks',
    items: [
      { path: '/', label: 'Dashboard', icon: BarChart3 },
      { path: '/middle-east-impact-dashboard', label: 'Middle East Impact', icon: Activity },
    ],
  },
  {
    title: 'Chokepoints',
    items: [{ path: '/strait-of-hormuz', label: 'Hormuz Hub', icon: MapPin }],
  },
  {
    title: 'Analysis',
    items: [
      { path: '/countries', label: 'Countries', icon: Globe },
      { path: '/tariffs', label: 'Tariffs', icon: TrendingUp },
      { path: '/trade-deals', label: 'Trade Deals', icon: FileText },
      { path: '/legislation', label: 'Legislation', icon: Scale },
      { path: '/compare', label: 'Compare GTI', icon: BarChart3 },
    ],
  },
  {
    title: 'Research',
    items: [{ path: '/insights', label: 'Insights', icon: BookOpen }],
  },
  {
    title: 'About',
    items: [
      { path: '/about', label: 'About', icon: FileText },
      { path: '/contact', label: 'Contact', icon: Mail },
    ],
  },
];

const TOP_PRIMARY_PATHS = ['/', '/countries', '/tariffs', '/compare', '/insights'];

function pathIsActive(pathname, itemPath) {
  if (itemPath === '/insights') return pathname.startsWith('/insights');
  if (itemPath === '/strait-of-hormuz') return pathname.startsWith('/strait-of-hormuz');
  return pathname === itemPath;
}

const Navbar = () => {
  const location = useLocation();
  const { isDark, toggleTheme } = useTheme();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const [insightsQuery, setInsightsQuery] = useState('');
  const moreRef = useRef(null);
  const isInsightsHub = location.pathname === '/insights';

  useEffect(() => {
    setMobileOpen(false);
    setInsightsQuery('');
  }, [location.pathname]);

  useEffect(() => {
    if (!isInsightsHub) return;
    emitInsightsSearchQuery(insightsQuery);
  }, [insightsQuery, isInsightsHub]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') {
        setMobileOpen(false);
        setMoreOpen(false);
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  useEffect(() => {
    const onClick = (e) => {
      if (moreRef.current && !moreRef.current.contains(e.target)) setMoreOpen(false);
    };
    document.addEventListener('click', onClick);
    return () => document.removeEventListener('click', onClick);
  }, []);

  const flatItems = NAV_GROUPS.flatMap((g) => g.items);
  const topItems = flatItems.filter((i) => TOP_PRIMARY_PATHS.includes(i.path));
  const moreItems = flatItems.filter((i) => !TOP_PRIMARY_PATHS.includes(i.path));

  return (
    <>
      <header className="fixed left-0 right-0 top-0 z-50 border-b border-outline-variant/10 bg-white dark:border-transparent dark:bg-surface">
        <div className="flex h-16 items-center justify-between gap-4 px-4 sm:px-6 lg:px-8">
          <div className="flex min-w-0 items-center gap-4 lg:gap-8">
            <button
              type="button"
              className="rounded-stitch-lg p-2 text-primary hover:bg-slate-100 dark:hover:bg-surface-container-low lg:hidden"
              aria-expanded={mobileOpen}
              aria-controls="mobile-nav-drawer"
              aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
              onClick={() => setMobileOpen((o) => !o)}
            >
              {mobileOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
            <Link
              to="/"
              className="shrink-0 text-xl font-bold uppercase tracking-tighter text-primary font-headline dark:text-primary"
            >
              TradeRadar
            </Link>
            <nav className="hidden items-center gap-6 md:flex" aria-label="Primary">
              {topItems.map((item) => {
                const active = pathIsActive(location.pathname, item.path);
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`font-headline text-sm font-bold uppercase tracking-tight transition-colors ${
                      active
                        ? 'border-b-2 border-primary-container pb-2 text-primary'
                        : 'border-b-2 border-transparent pb-2 text-outline hover:text-primary dark:text-outline'
                    }`}
                  >
                    {item.label}
                  </Link>
                );
              })}
              <div className="relative" ref={moreRef}>
                <button
                  type="button"
                  className="flex items-center gap-1 rounded-stitch-lg px-3 py-2 text-xs font-semibold uppercase tracking-tight text-outline hover:bg-slate-100 hover:text-primary dark:hover:bg-surface-container-low dark:hover:text-primary font-headline"
                  aria-expanded={moreOpen}
                  aria-haspopup="true"
                  onClick={() => setMoreOpen((v) => !v)}
                >
                  More
                  <ChevronDown className={`h-4 w-4 transition-transform ${moreOpen ? 'rotate-180' : ''}`} />
                </button>
                {moreOpen && (
                  <div
                    className="absolute right-0 top-full z-50 mt-1 min-w-[14rem] rounded-stitch-xl border border-outline-variant/15 bg-white py-2 shadow-intel-glow dark:border-outline-variant/20 dark:bg-surface-container dark:glass-panel"
                    role="menu"
                  >
                    {moreItems.map((item) => {
                      const Icon = item.icon;
                      const active = pathIsActive(location.pathname, item.path);
                      return (
                        <Link
                          key={item.path}
                          to={item.path}
                          role="menuitem"
                          className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium transition-colors ${
                            active
                              ? 'bg-primary-100 text-primary-700 dark:bg-surface-container-high dark:text-primary'
                              : 'text-slate-700 hover:bg-slate-50 dark:text-on-surface dark:hover:bg-surface-container-low'
                          }`}
                          onClick={() => setMoreOpen(false)}
                        >
                          <Icon className="h-4 w-4 shrink-0" aria-hidden />
                          {item.label}
                        </Link>
                      );
                    })}
                  </div>
                )}
              </div>
            </nav>
            {isInsightsHub && (
              <div className="hidden min-w-0 max-w-xs flex-1 items-center gap-2 rounded-stitch-lg border border-outline-variant/20 bg-slate-50 px-3 py-1.5 dark:bg-surface-container-low md:flex lg:max-w-md">
                <Search className="h-4 w-4 shrink-0 text-outline" aria-hidden />
                <input
                  type="search"
                  value={insightsQuery}
                  onChange={(e) => setInsightsQuery(e.target.value)}
                  placeholder="Search insights…"
                  className="min-w-0 flex-1 bg-transparent text-sm text-on-surface placeholder:text-on-surface-variant focus:outline-none"
                  aria-label="Filter insights articles"
                />
              </div>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={toggleTheme}
              className="rounded-stitch-lg p-2 text-outline transition-colors hover:bg-slate-100 hover:text-primary dark:hover:bg-surface-container-low dark:hover:text-primary"
              aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
              title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
            >
              {isDark ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
            </button>
          </div>
        </div>
      </header>

      {/* Desktop sidebar */}
      <aside
        className="fixed bottom-0 left-0 top-16 z-40 hidden w-64 flex-col border-r border-slate-200 bg-slate-100 dark:border-outline-variant/10 dark:bg-surface lg:flex"
        aria-label="Section navigation"
      >
        <div className="border-b border-slate-200 px-6 py-6 dark:border-outline-variant/10">
          <div className="font-headline text-lg font-bold text-primary-700 dark:text-primary">Global Intelligence</div>
          <div className="label-intel mt-1 text-[10px] text-slate-500 dark:text-outline">Active session</div>
        </div>
        <nav className="flex-1 overflow-y-auto px-3 py-4">
          {isInsightsHub && (
            <div className="mb-4 px-1 md:hidden">
              <div className="flex items-center gap-2 rounded-stitch-lg border border-outline-variant/20 bg-white px-3 py-2 dark:bg-surface-container-low">
                <Search className="h-4 w-4 shrink-0 text-outline" aria-hidden />
                <input
                  type="search"
                  value={insightsQuery}
                  onChange={(e) => setInsightsQuery(e.target.value)}
                  placeholder="Search insights…"
                  className="min-w-0 flex-1 bg-transparent text-sm focus:outline-none"
                  aria-label="Filter insights articles"
                />
              </div>
            </div>
          )}
          {NAV_GROUPS.map((group) => (
            <div key={group.title} className="mb-6">
              <div className="label-intel mb-2 px-3 text-slate-500 dark:text-on-surface-variant">{group.title}</div>
              <ul className="space-y-0.5">
                {group.items.map((item) => {
                  const active = pathIsActive(location.pathname, item.path);
                  const Icon = item.icon;
                  return (
                    <li key={item.path}>
                      <Link
                        to={item.path}
                        className={`flex items-center gap-3 rounded-stitch-lg px-3 py-2.5 text-xs font-semibold uppercase tracking-wide transition-colors font-headline ${
                          active
                            ? 'bg-white text-primary-700 shadow-sm dark:bg-surface-container-low dark:text-primary'
                            : 'text-slate-600 hover:bg-white/80 hover:text-slate-900 dark:text-on-surface-variant dark:hover:bg-surface-container-low/50 dark:hover:text-on-surface'
                        }`}
                      >
                        <Icon className="h-4 w-4 shrink-0" aria-hidden />
                        {item.label}
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
          <div className="mt-2 border-t border-slate-200 px-3 pt-4 dark:border-outline-variant/10">
            <Link
              to="/insights"
              className="mb-3 block rounded-stitch-lg bg-gradient-to-r from-primary-container via-primary to-blue-700 px-3 py-2.5 text-center text-[10px] font-bold uppercase tracking-wide text-white shadow-sm dark:from-primary dark:to-blue-900"
            >
              AI Insights
            </Link>
            <Link
              to="/insights/methodology"
              className="block rounded-stitch-lg px-3 py-2 text-xs font-medium text-slate-600 hover:bg-white/80 dark:text-on-surface-variant dark:hover:bg-surface-container-low/80"
            >
              Methodology
            </Link>
            <Link
              to="/editorial-standards"
              className="block rounded-stitch-lg px-3 py-2 text-xs font-medium text-slate-600 hover:bg-white/80 dark:text-on-surface-variant dark:hover:bg-surface-container-low/80"
            >
              Editorial standards
            </Link>
          </div>
        </nav>
      </aside>

      {/* Mobile drawer */}
      {mobileOpen && (
        <>
          <button
            type="button"
            className="fixed inset-0 z-40 bg-surface-container-lowest/60 backdrop-blur-sm lg:hidden dark:bg-black/50"
            aria-label="Close menu backdrop"
            onClick={() => setMobileOpen(false)}
          />
          <div
            id="mobile-nav-drawer"
            className="fixed bottom-0 left-0 top-16 z-50 w-[min(100%,20rem)] overflow-y-auto border-r border-outline-variant/15 bg-white shadow-intel-glow dark:border-outline-variant/20 dark:bg-surface-container lg:hidden"
            role="dialog"
            aria-modal="true"
            aria-label="Navigation menu"
          >
            <nav className="p-4">
              {NAV_GROUPS.map((group) => (
                <div key={group.title} className="mb-6">
                  <div className="label-intel mb-2 px-2 text-slate-500 dark:text-outline">{group.title}</div>
                  <ul className="space-y-1">
                    {group.items.map((item) => {
                      const active = pathIsActive(location.pathname, item.path);
                      const Icon = item.icon;
                      return (
                        <li key={item.path}>
                          <Link
                            to={item.path}
                            onClick={() => setMobileOpen(false)}
                            className={`flex items-center gap-3 rounded-stitch-lg px-3 py-2.5 text-xs font-semibold uppercase tracking-tight font-headline ${
                              active
                                ? 'bg-slate-100 text-primary-700 dark:bg-surface-container-low dark:text-primary'
                                : 'text-slate-700 hover:bg-slate-50 dark:text-on-surface-variant dark:hover:bg-surface-container-low/80 dark:hover:text-on-surface'
                            }`}
                          >
                            <Icon className="h-4 w-4 shrink-0" aria-hidden />
                            {item.label}
                          </Link>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              ))}
            </nav>
          </div>
        </>
      )}
    </>
  );
};

export default Navbar;
